<template>
  <div class="modal fade" tabindex="-1" data-backdrop="static" ref="modal">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <button class="close" type="button" @click="closeModal">
            <i class="fa fa-times"></i>
          </button>
          <h3>Version History</h3>
        </div>

        <div class="modal-body">
          <div class="alert alert-info" v-if="storyAuditsStore.untrackedAssociations.length > 0">
            <strong>Note:</strong>
            Reverting restores the story's main content (headline, body, byline, etc.) and attachments at that point in time.
            The following are not tracked and will remain unchanged: {{ storyAuditsStore.formattedUntrackedAssociations }}.
            <br><br>
            <strong>Version numbers:</strong> Some version numbers may be missing from the list. This is normal - 
            versions that only contain minor changes (like locking/unlocking the story) are automatically hidden to keep the history focused on meaningful content changes.
          </div>

          <div class="row" v-if="!storyAuditsStore.loading || storyAuditsStore.uniqueVersions.length > 0">
            <!-- Version list panel -->
            <div class="col-md-5">
              <h4>Versions</h4>
              <div class="version-list" v-if="storyAuditsStore.uniqueVersions.length > 0">
                <div
                  class="version-item"
                  v-for="audit in storyAuditsStore.uniqueVersions"
                  :key="audit.id"
                  :class="{ active: selectedVersion === audit.version, 'is-revert': isRevertAudit(audit) }"
                  @click="selectVersion(audit.version)"
                >
                  <div class="version-header">
                    <span class="version-number">Version {{ audit.version }}</span>
                    <span class="version-action" :class="audit.action">{{ getActionLabel(audit) }}</span>
                  </div>

                  <div class="version-meta">
                    <span class="version-user" v-if="audit.user">
                      <i class="fa fa-user"></i>
                      {{ audit.user.name }}
                    </span>
                    <span class="version-date">
                      <i class="fa fa-clock-o"></i>
                      {{ formatDateTime(audit.created_at) }}
                    </span>
                  </div>

                  <div class="version-comment" v-if="audit.comment">
                    <i class="fa fa-undo"></i>
                    {{ audit.comment }}
                  </div>

                  <div class="version-changes" v-if="audit.action === 'update' && hasMeaningfulChanges(audit.audited_changes)">
                    <div class="change-summary">
                      <i class="fa fa-pencil"></i>
                      Changed:
                      <span class="changed-fields">{{ formatChangedFields(audit.audited_changes) }}</span>
                    </div>
                  </div>
                  <div class="version-changes" v-else-if="audit.scope === 'attachment'">
                    <div class="change-summary">
                      <i class="fa fa-paperclip"></i>
                      <span class="changed-fields">{{ getAttachmentSummary(audit) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="no-audits" v-else>
                <p>No version history available.</p>
              </div>
            </div>

            <!-- Preview panel -->
            <div class="col-md-7">
              <div class="preview-panel" v-if="storyAuditsStore.revisionPreview">
                <h4>Preview: Version {{ storyAuditsStore.revisionVersion }}</h4>

                <div class="preview-actions">
                  <button
                    class="btn btn-warning"
                    @click="confirmRevert"
                    :disabled="storyAuditsStore.reverting || isLatestVersion"
                  >
                    <i class="fa fa-undo" v-if="!storyAuditsStore.reverting"></i>
                    <i class="fa fa-circle-o-notch fa-spin" v-else></i>
                    {{ isLatestVersion ? 'Current Version' : 'Revert to This Version' }}
                  </button>
                </div>

                <!-- View mode toggle -->
                <div class="view-toggle" v-if="selectedAudit && selectedAudit.action === 'update'">
                  <div class="btn-group">
                    <button
                      class="btn btn-sm"
                      :class="viewMode === 'diff' ? 'btn-primary' : 'btn-default'"
                      @click="viewMode = 'diff'"
                    >
                      <i class="fa fa-columns"></i>
                      Show Changes
                    </button>
                    <button
                      class="btn btn-sm"
                      :class="viewMode === 'preview' ? 'btn-info' : 'btn-default'"
                      @click="viewMode = 'preview'"
                    >
                      <i class="fa fa-eye"></i>
                      Preview
                    </button>
                  </div>
                </div>

                <!-- Preview content view -->
                <div class="preview-content" v-if="viewMode === 'preview'">
                  <PreviewField label="Slug" :value="storyAuditsStore.revisionPreview.slug" :changed="isFieldChanged('slug')" />
                  <PreviewField label="Headline" :value="storyAuditsStore.revisionPreview.headline" :changed="isFieldChanged('headline')" />
                  <PreviewField label="Deck head" :value="storyAuditsStore.revisionPreview.drop_head" :changed="isFieldChanged('drop_head')" />
                  <PreviewField label="Byline" :value="storyAuditsStore.revisionPreview.byline" :html="true" :changed="isFieldChanged('byline')" />
                  <PreviewField label="Content" :value="storyAuditsStore.revisionPreview.content" :html="true" :multiLine="true" :changed="isFieldChanged('content')" />
                  <PreviewField label="Web summary" :value="storyAuditsStore.revisionPreview.web_summary" :html="true" :changed="isFieldChanged('web_summary')" />
                  <PreviewField label="Notes" :value="storyAuditsStore.revisionPreview.notes" :multiLine="true" :changed="isFieldChanged('notes')" />
                </div>

                <!-- Diff view showing before/after -->
                <div v-else-if="viewMode === 'diff' && selectedAudit">
                  <AttachmentView
                    v-if="selectedAudit.scope === 'attachment'"
                    :audit="selectedAudit"
                  />
                  <DiffView
                    v-else
                    :audited-changes="meaningfulChangesForSelectedVersion"
                    :field-labels="fieldLabels"
                  />
                </div>
              </div>

              <div class="preview-placeholder" v-else>
                <div class="text-center text-muted">
                  <i class="fa fa-history fa-3x"></i>
                  <p>Select a version to preview its content</p>
                </div>
              </div>
            </div>
          </div>

          <div class="loading-indicator" v-if="storyAuditsStore.loading && storyAuditsStore.audits.length === 0">
            <i class="fa fa-circle-o-notch fa-spin fa-2x"></i>
            <p>Loading version history...</p>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-default" @click="closeModal">Close</button>
        </div>
      </div>
    </div>

    <!-- Revert confirmation dialog -->
    <div class="modal fade" tabindex="-1" ref="confirmModal" data-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h4>Confirm Revert</h4>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to revert to version {{ storyAuditsStore.revisionVersion }}?</p>
            <p class="text-warning">
              <i class="fa fa-exclamation-triangle"></i>
              This will replace the current story content with the content from the selected version.
              A new version will be created to record this change.
            </p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-link" @click="cancelRevert">Cancel</button>
            <button class="btn btn-warning" @click="executeRevert" :disabled="storyAuditsStore.reverting">
              <i class="fa fa-undo" v-if="!storyAuditsStore.reverting"></i>
              <i class="fa fa-circle-o-notch fa-spin" v-else></i>
              {{ storyAuditsStore.reverting ? 'Reverting...' : 'Revert' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapStores } from 'pinia'
  import { useStoryAuditsStore } from '@/stores/story_audits'
  import { formatDateTime } from '@/utils/time_helpers'
  import { AUDIT_FIELD_LABELS, getActionLabel } from '@/constants/audit_constants'
  import PreviewField from './audit_history/preview_field.vue'
  import DiffView from './audit_history/diff_view.vue'
  import AttachmentView from './audit_history/attachment_view.vue'

  export default {
    components: {
      PreviewField,
      DiffView,
      AttachmentView
    },

    props: {
      storyId: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        selectedVersion: null,
        viewMode: 'diff' // 'diff' or 'preview'
      }
    },

    computed: {
      ...mapStores(useStoryAuditsStore),

      isLatestVersion() {
        return this.selectedVersion != null &&
               this.selectedVersion === this.storyAuditsStore.latestVersion
      },

      selectedAudit() {
        if (!this.selectedVersion) return null
        return this.storyAuditsStore.byVersion(this.selectedVersion)
      },

      changedFieldsInVersion() {
        if (!this.selectedAudit || !this.selectedAudit.audited_changes) return []
        const meaningfulChanges = this.storyAuditsStore.getMeaningfulChanges(this.selectedAudit.audited_changes)
        return Object.keys(meaningfulChanges)
      },

      meaningfulChangesForSelectedVersion() {
        if (!this.selectedAudit || !this.selectedAudit.audited_changes) return {}
        return this.storyAuditsStore.getMeaningfulChanges(this.selectedAudit.audited_changes)
      },

      // Use shared constants for field labels
      fieldLabels() {
        return AUDIT_FIELD_LABELS
      }
    },

    watch: {
      'storyAuditsStore.showModal': function(show) {
        if (show) {
          jQuery(this.$refs.modal).modal({ show: true })
        } else {
          jQuery(this.$refs.modal).modal('hide')
        }
      }
    },

    mounted() {
      // Clean up modal when component is destroyed
      jQuery(this.$refs.modal).on('hidden.bs.modal', () => {
        this.storyAuditsStore.closeModal()
      })
    },

    beforeUnmount() {
      jQuery(this.$refs.modal).off('hidden.bs.modal')
    },

    methods: {
      formatDateTime,

      getActionLabel(audit) {
        return getActionLabel(audit.action, audit.scope || 'story')
      },

      getAttachmentSummary(audit) {
        if (audit.action === 'create') {
          return 'Attachment added'
        } else if (audit.action === 'destroy') {
          return 'Attachment removed'
        } else if (audit.action === 'update') {
          return 'Attachment modified'
        }
        return 'Attachment change'
      },

      closeModal() {
        this.storyAuditsStore.closeModal()
        this.selectedVersion = null
        this.viewMode = 'diff'
      },

      selectVersion(version) {
        this.selectedVersion = version
        this.storyAuditsStore.fetchRevision(version)
      },

      countChanges(auditedChanges) {
        const meaningful = this.storyAuditsStore.getMeaningfulChanges(auditedChanges)
        return meaningful ? Object.keys(meaningful).length : 0
      },

      hasMeaningfulChanges(auditedChanges) {
        return this.countChanges(auditedChanges) > 0
      },

      formatChangedFields(auditedChanges) {
        if (!auditedChanges) return ''
        const meaningfulChanges = this.storyAuditsStore.getMeaningfulChanges(auditedChanges)
        const fields = Object.keys(meaningfulChanges)
        const humanized = fields.map(f => this.storyAuditsStore.humanizeFieldName(f))
        if (humanized.length <= 3) {
          return humanized.join(', ')
        }
        return `${humanized.slice(0, 3).join(', ')} +${humanized.length - 3} more`
      },

      isFieldChanged(fieldName) {
        return this.changedFieldsInVersion.includes(fieldName)
      },

      isRevertAudit(audit) {
        return audit.comment && audit.comment.startsWith('Reverted to version')
      },

      confirmRevert() {
        jQuery(this.$refs.confirmModal).modal({ show: true })
      },

      cancelRevert() {
        jQuery(this.$refs.confirmModal).modal('hide')
      },

      async executeRevert() {
        try {
          const story = await this.storyAuditsStore.revertToVersion(this.storyAuditsStore.revisionVersion)
          jQuery(this.$refs.confirmModal).modal('hide')

          // Refresh the story data in the main store
          try {
            await this.$store.dispatch('story/fetch', { id: this.storyId })
          } catch (err) {
            console.error('Failed to refresh story data:', err)
          }
          
          // Explicitly refresh attachments
          try {
            await this.$store.dispatch('storyMedia/fetchForStory', this.storyId)
          } catch (err) {
            console.error('Failed to refresh attachments:', err)
          }

          // Reload audit history to include the new revert audit
          try {
            await this.storyAuditsStore.fetchAudits(this.storyId)
          } catch (err) {
            console.error('Failed to reload audit history:', err)
          }

          // Reset selection since we have a new version now
          this.selectedVersion = null
        } catch (err) {
          // Error already handled by store
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .modal-dialog.modal-lg {
    width: 900px;
  }

  .version-list {
    max-height: 500px;
    overflow-y: auto;
    border: 1px solid #ddd;
    border-radius: 4px;
  }

  .version-item {
    padding: 12px;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: #f5f5f5;
    }

    &.active {
      background-color: #e8f4fc;
      border-left: 3px solid #337ab7;
    }

    &.is-revert {
      background-color: #fcf8e3;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  .version-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
  }

  .attachment-label {
    font-size: 12px;
    color: #555;
    margin-top: 4px;
    padding: 4px 8px;
    background-color: #f9f9f9;
    border-left: 3px solid #8a6d3b;
    border-radius: 3px;
    
    i {
      margin-right: 4px;
      color: #8a6d3b;
    }
  }

  .version-number {
    font-weight: bold;
  }

  .version-action {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px;
    text-transform: uppercase;

    &.create {
      background-color: #dff0d8;
      color: #3c763d;
    }

    &.update {
      background-color: #d9edf7;
      color: #31708f;
    }

    &.destroy {
      background-color: #f2dede;
      color: #a94442;
    }
  }

  .version-meta {
    font-size: 12px;
    color: #777;
    display: flex;
    gap: 12px;
  }

  .version-comment {
    font-size: 12px;
    color: #8a6d3b;
    margin-top: 4px;
    font-style: italic;
  }

  .version-changes {
    font-size: 11px;
    color: #666;
    margin-top: 6px;

    .change-summary {
      display: flex;
      align-items: flex-start;
      gap: 4px;

      i {
        color: #8a6d3b;
        margin-top: 2px;
      }
    }

    .changed-fields {
      color: #8a6d3b;
      font-weight: 500;
    }
  }

  .preview-panel {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 15px;
    background-color: #fafafa;
  }

  .preview-actions {
    margin-bottom: 15px;
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .view-toggle {
    margin-bottom: 15px;

    .btn-group {
      display: flex;

      .btn {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }

  .preview-content {
    max-height: 400px;
    overflow-y: auto;
  }

  .preview-placeholder {
    padding: 60px 20px;
    text-align: center;
    color: #999;

    i {
      margin-bottom: 15px;
    }
  }

  .loading-indicator {
    text-align: center;
    padding: 40px;
    color: #777;

    p {
      margin-top: 15px;
    }
  }

  .no-audits {
    padding: 20px;
    text-align: center;
    color: #777;
  }
</style>
